import { useMemo, useState } from "react";
import type { QuizBlock } from "../contract/types";
import { Icon } from "../components/Icon";
import { RichText } from "../components/RichText";
import type { BlockViewProps } from "./types";

/**
 * Pregunta de control con revelado.
 *
 * Comprueba comprensión, no pone nota: el estudiante puede revelar la
 * respuesta sin responder. La comprobación y el revelado son locales:
 * no envían mensajes ni inician un nuevo turno del asistente.
 */

type QuizKind = NonNullable<QuizBlock["kind"]>;

const KIND_LABEL: Record<QuizKind, string> = {
  single_choice: "Opción única",
  multiple_choice: "Opción múltiple",
  true_false: "Verdadero o falso",
};

function sameSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((item) => set.has(item));
}

export function Quiz({ block }: BlockViewProps<QuizBlock>) {
  const kind: QuizKind = block.kind ?? "single_choice";
  const options = useMemo(() => block.options ?? [], [block.options]);
  const correct = useMemo(() => new Set(block.correct ?? []), [block.correct]);

  const [selected, setSelected] = useState<string[]>([]);
  const [revealed, setRevealed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showHint, setShowHint] = useState(false);

  const isCorrect = sameSet(selected, [...correct]);
  const answered = selected.length > 0;

  function toggle(optionId: string) {
    if (submitted || revealed) return;
    setSelected((current) => {
      if (kind === "multiple_choice") {
        return current.includes(optionId)
          ? current.filter((id) => id !== optionId)
          : [...current, optionId];
      }
      return [optionId];
    });
  }

  function submit() {
    if (!answered || submitted) return;
    setSubmitted(true);
  }

  function optionState(optionId: string): string {
    const isSelected = selected.includes(optionId);
    const classes = ["quiz__option"];
    if (isSelected) classes.push("quiz__option--selected");
    if (submitted || revealed) {
      if (correct.has(optionId)) classes.push("quiz__option--correct");
      else if (isSelected) classes.push("quiz__option--incorrect");
    }
    return classes.join(" ");
  }

  return (
    <div className="atom atom--quiz quiz" data-submitted={submitted ? "true" : "false"}>
      <div className="quiz__head">
        <span className="quiz__kind u-label">{KIND_LABEL[kind]}</span>
        {block.module ? <span className="quiz__module">{block.module}</span> : null}
      </div>

      <RichText className="quiz__question">{block.question}</RichText>

      <ul className="quiz__options" role={kind === "multiple_choice" ? "group" : "radiogroup"}>
        {options.map((option) => {
          const isSelected = selected.includes(option.id);
          const isRight = correct.has(option.id);
          const showMark = submitted || revealed;
          return (
            <li key={option.id}>
              <button
                type="button"
                className={optionState(option.id)}
                role={kind === "multiple_choice" ? "checkbox" : "radio"}
                aria-checked={isSelected}
                disabled={submitted || revealed}
                onClick={() => toggle(option.id)}
              >
                <span className="quiz__marker" aria-hidden="true">
                  {showMark && isRight ? <Icon name="check" size="0.75rem" /> : null}
                  {showMark && !isRight && isSelected ? <Icon name="x" size="0.75rem" /> : null}
                </span>
                <span className="quiz__option-text">{option.text}</span>
              </button>
            </li>
          );
        })}
      </ul>

      <div className="quiz__footer">
        {!submitted && !revealed ? (
          <>
            <button
              type="button"
              className="u-button u-button--primary"
              disabled={!answered}
              onClick={submit}
            >
              Comprobar
            </button>
            {block.hint ? (
              <button
                type="button"
                className="u-button u-button--ghost"
                aria-expanded={showHint}
                onClick={() => setShowHint((value) => !value)}
              >
                {showHint ? "Ocultar pista" : "Ver pista"}
              </button>
            ) : null}
            <button
              type="button"
              className="u-button u-button--ghost"
              onClick={() => setRevealed(true)}
            >
              Revelar respuesta
            </button>
          </>
        ) : null}

        {submitted && !revealed ? (
          <p
            className={`quiz__verdict quiz__verdict--${isCorrect ? "correct" : "incorrect"}`}
            role="status"
          >
            <Icon name={isCorrect ? "check-circle" : "x"} size="1rem" />
            <span>
              {isCorrect
                ? "Correcto."
                : `Incorrecto: la respuesta correcta es ${[...correct].join(", ").toUpperCase()}.`}
            </span>
          </p>
        ) : null}
      </div>

      {showHint && block.hint && !submitted && !revealed ? (
        <div className="quiz__hint">
          <Icon name="lightbulb" size="0.875rem" />
          <RichText className="quiz__hint-text">{block.hint}</RichText>
        </div>
      ) : null}

      {revealed && !submitted ? (
        <p className="quiz__revealed">
          <Icon name="info" size="0.875rem" />
          <span>Respuesta correcta: {[...correct].join(", ").toUpperCase()}.</span>
        </p>
      ) : null}

      {(submitted || revealed) && block.explanation ? (
        <div className="quiz__explanation">
          <span className="quiz__explanation-label u-label">Por qué</span>
          <RichText className="quiz__explanation-text">{block.explanation}</RichText>
        </div>
      ) : null}

    </div>
  );
}

export default Quiz;
